import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP08 — "3 Números que Todo Dono Deveria Saber de Cabeça"
 * Editing Style: DYNAMIC (Dark Premium)
 * Profile: @guilhermeribeiro.ai (foto real)
 * Copy: BoardCopy (Halbert + Hormozi + Sugarman)
 * Audio: ElevenLabs TTS per-scene (voz Guilherme)
 * IDV: Verde #00DF82, Amarelo #FECB18, Azul #1E3A61
 */

// ─── PALETA CAFÉ ───────────────────────────────────────────────────
const ORANGE = "#FF6B35";
const ORANGE_DARK = "#D94F1F";
const GREEN = IDV.secondary; // #00DF82
const RED = IDV.error;       // #F87171
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const DARK_MUTED = "#6E6E76";
const BLACK = "#000000";
const CARD_BG = "linear-gradient(145deg, #131315 0%, #0E0E10 100%)";

const SZ = SAFE_ZONES.universal;

// ─── AUDIO TIMING (synced to ElevenLabs) ───────────────────────────
const SCENES = [
  {dur: 126, file: "audio/cafe_ep08/cafe_ep08_c01.mp3", dir: "right" as const},  // 3.71s + 15f
  {dur: 392, file: "audio/cafe_ep08/cafe_ep08_c02.mp3", dir: "left" as const},   // 12.56s + 15f
  {dur: 647, file: "audio/cafe_ep08/cafe_ep08_c03.mp3", dir: "up" as const},     // 21.05s + 15f
  {dur: 354, file: "audio/cafe_ep08/cafe_ep08_c04.mp3", dir: "right" as const},  // 11.31s + 15f
  {dur: 137, file: "audio/cafe_ep08/cafe_ep08_c05.mp3", dir: "up" as const},     // 4.05s + 15f
];

export const CAFE_EP08_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── PREMIUM CARD ──────────────────────────────────────────────────
const premiumCard: React.CSSProperties = {
  background: CARD_BG,
  borderRadius: 24,
  border: "1px solid rgba(255, 255, 255, 0.06)",
  boxShadow: `
    0 0 0 0.5px rgba(255, 255, 255, 0.03),
    0 2px 4px rgba(0, 0, 0, 0.4),
    0 8px 24px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.04)
  `,
};

// ─── BACKGROUND 3 CAMADAS ──────────────────────────────────────────
const BG: React.FC<{color?: string; radial?: string}> = ({
  color = BLACK,
  radial = "rgba(255,107,53,0.12)",
}) => (
  <>
    <AbsoluteFill style={{backgroundColor: color}} />
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(ellipse at 50% 40%, ${radial} 0%, transparent 70%)`,
      }}
    />
    <Noise opacity={0.03} />
  </>
);

// ─── SCENE 1: ABERTURA RITUAL ──────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const titleSpring = spring({frame: frame - 20, fps, config: SPRINGS.bouncy});
  const subtitleP = interpolate(frame, [40, 55], [0, 1], CLAMP);

  // Steam animation
  const steamPhase = (i: number) => ((frame + i * 10) % 60) / 60;

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep08">
    <AbsoluteFill>
      <AbsoluteFill
        style={{
          background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DARK} 100%)`,
        }}
      />
      <Noise opacity={0.04} />
      <Audio src={staticFile(SCENES[0].file)} volume={0.9} />

      <div
        style={{
          position: "absolute",
          top: "42%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: 900,
        }}
      >
        {/* BOM DIA */}
        <div
          style={{
            fontSize: 52,
            fontWeight: 300,
            color: "rgba(255,255,255,0.9)",
            letterSpacing: 12,
            textTransform: "uppercase",
            marginBottom: 20,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [0, 15], [0, 1], CLAMP),
            transform: `translateY(${interpolate(frame, [0, 15], [40, 0], CLAMP)}px)`,
          }}
        >
          BOM DIA
        </div>

        {/* Xícara SVG */}
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          style={{
            marginBottom: 24,
            opacity: interpolate(frame, [10, 25], [0, 1], CLAMP),
          }}
        >
          {[0, 1, 2].map((i) => (
            <path
              key={i}
              d={`M${28 + i * 12} 10 Q${28 + i * 12} 5 ${32 + i * 12} 5 T${36 + i * 12} 10`}
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity={interpolate(
                steamPhase(i),
                [0, 0.5, 1],
                [0, 1, 0],
                CLAMP
              )}
              transform={`translateY(${interpolate(frame, [15 + i * 3, 80], [0, -15], CLAMP)})`}
            />
          ))}
          <path
            d="M15 25 L15 50 Q15 58 23 58 L50 58 Q58 58 58 50 L58 25 M15 25 L58 25"
            stroke="#FFFFFF"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={180}
            strokeDashoffset={interpolate(frame, [15, 35], [180, 0], CLAMP)}
          />
          <path
            d="M62 30 L66 30 Q70 30 70 34 L70 42 Q70 46 66 46 L62 46"
            stroke="#FFFFFF"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={40}
            strokeDashoffset={interpolate(frame, [25, 40], [40, 0], CLAMP)}
          />
        </svg>

        {/* Logo Café com teu ROI */}
        <div style={{marginBottom: 16}}>
          <Img
            src={staticFile("assets/profiles/logo-cafe-roi-dark-bg.png")}
            style={{
              height: 80,
              objectFit: "contain",
              opacity: interpolate(frame, [18, 30], [0, 1], CLAMP),
              transform: `scale(${interpolate(titleSpring, [0, 1], [0.85, 1])})`,
            }}
          />
        </div>

        {/* CAFÉ COM TEU ROI */}
        <div
          style={{
            fontSize: 84,
            fontWeight: 900,
            color: WHITE,
            letterSpacing: -3,
            textTransform: "uppercase",
            lineHeight: 0.95,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [20, 35], [0, 1], CLAMP),
            transform: `scale(${interpolate(titleSpring, [0, 1], [0.85, 1])})`,
          }}
        >
          CAFÉ COM
          <br />
          TEU ROI
        </div>

        {/* Subtitle */}
        <div
          style={{
            fontSize: 28,
            fontWeight: 400,
            color: "rgba(255,255,255,0.85)",
            letterSpacing: 0.5,
            marginTop: 20,
            fontFamily: FONT_STACK,
            opacity: subtitleP,
          }}
        >
          Pega o café e presta atenção.
        </div>
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 2: PRINCÍPIO — 3 números, não 30, não 50 ───────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const cardSpring = spring({frame: frame - 35, fps, config: SPRINGS.snappy});
  const num3Spring = spring({frame: frame - 60, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill>
      <BG radial="rgba(255,107,53,0.1)" />
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />

      {/* Label "PRINCÍPIO DE HOJE" */}
      <div
        style={{
          position: "absolute",
          top: SZ.top + 80,
          left: SZ.left,
          right: SZ.right,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: ORANGE,
            letterSpacing: 2,
            textTransform: "uppercase",
            marginBottom: 16,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [10, 25], [0, 1], CLAMP),
          }}
        >
          PRINCÍPIO DE HOJE
        </div>

        <div
          style={{
            fontSize: 64,
            fontWeight: 800,
            color: WHITE,
            letterSpacing: -2,
            lineHeight: 1.1,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [15, 35], [0, 1], CLAMP),
            transform: `translateX(${interpolate(frame, [15, 35], [-80, 0], CLAMP)}px)`,
            filter: `blur(${interpolate(frame, [15, 35], [8, 0], CLAMP)}px)`,
          }}
        >
          TODO DONO DEVERIA
          <br />
          SABER <span style={{color: ORANGE}}>3 NÚMEROS</span>
          <br />
          DE CABEÇA
        </div>
      </div>

      {/* Grid comparativo 3 colunas */}
      <div
        style={{
          position: "absolute",
          top: "55%",
          left: "50%",
          width: 880,
          height: 420,
          transform: `translate(-50%, -50%) translateY(${interpolate(cardSpring, [0, 1], [60, 0])}px)`,
          ...premiumCard,
          padding: 40,
          opacity: interpolate(frame, [35, 50], [0, 1], CLAMP),
        }}
      >
        <div style={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, height: "100%"}}>
          {/* 3 — CORRETO */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0, 223, 130, 0.1)",
              border: `2px solid rgba(0, 223, 130, 0.4)`,
              borderRadius: 16,
              padding: 24,
              opacity: interpolate(frame, [60, 75], [0, 1], CLAMP),
              transform: `scale(${interpolate(num3Spring, [0, 1], [0.9, 1])})`,
              boxShadow: `0 0 0 ${interpolate(Math.sin(frame * 0.1), [-1, 1], [0, 4], CLAMP)}px ${GREEN}60`,
            }}
          >
            <div style={{fontSize: 96, fontWeight: 200, color: GREEN, letterSpacing: -4, marginBottom: 12}}>
              3
            </div>
            <div style={{fontSize: 14, fontWeight: 600, color: GREEN, textTransform: "uppercase", letterSpacing: 1.5}}>
              ✓ CORRETO
            </div>
            <div style={{fontSize: 12, color: MUTED, textAlign: "center", marginTop: 8, fontFamily: FONT_STACK}}>
              Fácil de lembrar
            </div>
          </div>

          {/* 30 — ERRADO */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(248, 113, 113, 0.08)",
              border: "2px solid rgba(248, 113, 113, 0.3)",
              borderRadius: 16,
              padding: 24,
              opacity: interpolate(frame, [70, 85], [0, 0.6], CLAMP),
            }}
          >
            <div style={{fontSize: 96, fontWeight: 200, color: RED, letterSpacing: -4, marginBottom: 12, textDecoration: "line-through"}}>
              30
            </div>
            <div style={{fontSize: 14, fontWeight: 600, color: RED, textTransform: "uppercase", letterSpacing: 1.5}}>
              ✗ MUITOS
            </div>
            <div style={{fontSize: 12, color: MUTED, textAlign: "center", marginTop: 8, fontFamily: FONT_STACK}}>
              Não cola na mente
            </div>
          </div>

          {/* 50 — ERRADO */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(248, 113, 113, 0.08)",
              border: "2px solid rgba(248, 113, 113, 0.3)",
              borderRadius: 16,
              padding: 24,
              opacity: interpolate(frame, [80, 95], [0, 0.6], CLAMP),
            }}
          >
            <div style={{fontSize: 96, fontWeight: 200, color: RED, letterSpacing: -4, marginBottom: 12, textDecoration: "line-through"}}>
              50
            </div>
            <div style={{fontSize: 14, fontWeight: 600, color: RED, textTransform: "uppercase", letterSpacing: 1.5}}>
              ✗ PARALISIA
            </div>
            <div style={{fontSize: 12, color: MUTED, textAlign: "center", marginTop: 8, fontFamily: FONT_STACK}}>
              Análise infinita
            </div>
          </div>
        </div>
      </div>

      {/* Contexto inferior */}
      <div
        style={{
          position: "absolute",
          bottom: SZ.bottom + 60,
          left: 80,
          right: 80,
          textAlign: "center",
          opacity: interpolate(frame, [110, 130], [0, 1], CLAMP),
        }}
      >
        <div style={{fontSize: 36, fontWeight: 500, color: WHITE, lineHeight: 1.4, fontFamily: FONT_STACK}}>
          Se você não sabe nenhum deles,
          <br />
          <span style={{color: RED}}>tá pilotando no escuro.</span>
        </div>
        <div style={{fontSize: 32, fontWeight: 400, color: MUTED, marginTop: 16, fontFamily: FONT_STACK}}>
          E o pior: pagando alguém pra
          <br />
          <span style={{color: ORANGE, fontWeight: 600}}>ligar a lanterna errada.</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 3: OS 3 NÚMEROS (revelação progressiva) ─────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();

  const containerMorphH = interpolate(frame, [0, 30], [420, 1100], CLAMP);
  const containerMorphW = interpolate(frame, [0, 30], [880, 900], CLAMP);

  return (
    <AbsoluteFill>
      <BG radial="rgba(255,107,53,0.08)" />
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {/* Container morph do card anterior */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: containerMorphW,
          height: containerMorphH,
          transform: "translate(-50%, -50%)",
          ...premiumCard,
          padding: 40,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 32,
            opacity: interpolate(frame, [0, 15], [0, 1], CLAMP),
          }}
        >
          <div style={{fontSize: 16, fontWeight: 600, color: ORANGE, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontFamily: FONT_STACK}}>
            OS 3 NÚMEROS
          </div>
          <div style={{fontSize: 24, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>
            que todo dono deveria saber
          </div>
        </div>

        {/* NÚMERO 1 — Custo por Cliente Novo */}
        <div
          style={{
            marginBottom: 40,
            paddingBottom: 40,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            opacity: interpolate(frame, [60, 80], [0, 1], CLAMP),
            transform: `translateX(${interpolate(frame, [60, 80], [-60, 0], CLAMP)}px)`,
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 16}}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(255, 107, 53, 0.2)",
                border: "2px solid rgba(255, 107, 53, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                fontWeight: 700,
                color: ORANGE,
                flexShrink: 0,
                fontFamily: FONT_STACK,
              }}
            >
              1
            </div>
            <div style={{fontSize: 32, fontWeight: 700, color: WHITE, lineHeight: 1.2, fontFamily: FONT_STACK}}>
              Custo por Cliente Novo
            </div>
          </div>
          <div style={{paddingLeft: 76, fontSize: 22, fontWeight: 400, color: MUTED, lineHeight: 1.5, fontFamily: FONT_STACK}}>
            Não um lead. Não um clique.
            <br />
            <span style={{color: WHITE}}>Um cliente que pagou.</span>
          </div>
          <div
            style={{
              marginTop: 16,
              marginLeft: 76,
              padding: 16,
              background: "rgba(255, 255, 255, 0.04)",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              opacity: interpolate(frame, [85, 100], [0, 1], CLAMP),
            }}
          >
            <div style={{fontSize: 16, color: MUTED, fontFamily: FONT_STACK}}>Exemplo:</div>
            <div style={{fontSize: 28, fontWeight: 600, color: ORANGE, fontFamily: "SF Mono, Menlo, monospace"}}>
              R$ {interpolate(frame, [100, 130], [0, 287], CLAMP).toFixed(0)}
            </div>
          </div>
        </div>

        {/* NÚMERO 2 — Valor ao Longo do Tempo */}
        <div
          style={{
            marginBottom: 40,
            paddingBottom: 40,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            opacity: interpolate(frame, [180, 200], [0, 1], CLAMP),
            transform: `translateX(${interpolate(frame, [180, 200], [-60, 0], CLAMP)}px)`,
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 16}}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(0, 223, 130, 0.2)",
                border: `2px solid rgba(0, 223, 130, 0.5)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                fontWeight: 700,
                color: GREEN,
                flexShrink: 0,
                fontFamily: FONT_STACK,
              }}
            >
              2
            </div>
            <div style={{fontSize: 32, fontWeight: 700, color: WHITE, lineHeight: 1.2, fontFamily: FONT_STACK}}>
              Valor ao Longo do Tempo
            </div>
          </div>
          <div style={{paddingLeft: 76, fontSize: 22, fontWeight: 400, color: MUTED, lineHeight: 1.5, fontFamily: FONT_STACK}}>
            Compra uma vez e some?
            <br />
            <span style={{color: WHITE}}>Ou volta 3, 4 vezes?</span>
          </div>
          <div style={{marginTop: 16, marginLeft: 76, display: "flex", gap: 12}}>
            <div
              style={{
                flex: 1,
                padding: 16,
                background: "rgba(248, 113, 113, 0.08)",
                border: "1px solid rgba(248, 113, 113, 0.3)",
                borderRadius: 12,
                textAlign: "center",
                opacity: interpolate(frame, [205, 220], [0, 1], CLAMP),
              }}
            >
              <div style={{fontSize: 14, color: DARK_MUTED, marginBottom: 8, fontFamily: FONT_STACK}}>1x compra</div>
              <div style={{fontSize: 24, fontWeight: 600, color: RED}}>R$ 100</div>
            </div>
            <div
              style={{
                flex: 1,
                padding: 16,
                background: "rgba(0, 223, 130, 0.1)",
                border: `1px solid rgba(0, 223, 130, 0.3)`,
                borderRadius: 12,
                textAlign: "center",
                opacity: interpolate(frame, [220, 235], [0, 1], CLAMP),
              }}
            >
              <div style={{fontSize: 14, color: DARK_MUTED, marginBottom: 8, fontFamily: FONT_STACK}}>4x compras</div>
              <div style={{fontSize: 24, fontWeight: 600, color: GREEN}}>R$ 420</div>
            </div>
          </div>
        </div>

        {/* NÚMERO 3 — Tempo até 1º Pagamento */}
        <div
          style={{
            opacity: interpolate(frame, [300, 320], [0, 1], CLAMP),
            transform: `translateX(${interpolate(frame, [300, 320], [-60, 0], CLAMP)}px)`,
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: 20, marginBottom: 16}}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(30, 58, 97, 0.3)",
                border: `2px solid rgba(30, 58, 97, 0.6)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                fontWeight: 700,
                color: "#60A5FA",
                flexShrink: 0,
                fontFamily: FONT_STACK,
              }}
            >
              3
            </div>
            <div style={{fontSize: 32, fontWeight: 700, color: WHITE, lineHeight: 1.2, fontFamily: FONT_STACK}}>
              Tempo até 1º Pagamento
            </div>
          </div>
          <div style={{paddingLeft: 76, fontSize: 22, fontWeight: 400, color: MUTED, lineHeight: 1.5, fontFamily: FONT_STACK}}>
            Se leva 90 dias e você aguenta 60...
            <br />
            <span style={{color: RED, fontWeight: 600}}>
              o marketing não falhou.
              <br />
              O caixa falhou antes.
            </span>
          </div>

          {/* Timeline bar */}
          <div
            style={{
              marginTop: 20,
              marginLeft: 76,
              position: "relative",
              opacity: interpolate(frame, [330, 350], [0, 1], CLAMP),
            }}
          >
            <div
              style={{
                height: 4,
                background: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${interpolate(frame, [350, 400], [0, 100], CLAMP)}%`,
                  background: "linear-gradient(90deg, #60A5FA 0%, #F87171 100%)",
                  boxShadow: "0 0 8px rgba(96, 165, 250, 0.6)",
                }}
              />
            </div>
            <div style={{display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 14, fontFamily: FONT_STACK}}>
              <div style={{color: "#60A5FA", fontWeight: 600}}>Dia 0</div>
              <div style={{color: RED, fontWeight: 600}}>Dia 60 (limite)</div>
              <div style={{color: MUTED}}>Dia 90</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lettering inferior */}
      <div
        style={{
          position: "absolute",
          bottom: SZ.bottom + 20,
          left: 60,
          right: 60,
          textAlign: "center",
          opacity: interpolate(frame, [420, 440], [0, 1], CLAMP),
        }}
      >
        <div style={{fontSize: 28, fontWeight: 600, color: WHITE, fontFamily: FONT_STACK}}>
          Esses 3 números são a diferença entre
          <br />
          <span style={{color: ORANGE}}>decidir com dados</span> ou{" "}
          <span style={{color: RED}}>achismo</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: DESAFIO — Calculadora interativa ─────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const cardSpring = spring({frame: frame - 15, fps, config: SPRINGS.snappy});
  const resultSpring = spring({frame: frame - 230, fps, config: SPRINGS.bouncy});
  const resultPulse = frame > 260 ? 1 + Math.sin((frame - 260) * 0.12) * 0.015 : 1;

  return (
    <AbsoluteFill>
      <BG radial="rgba(248,113,113,0.08)" />
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />

      {/* Lettering Superior */}
      <div
        style={{
          position: "absolute",
          top: SZ.top + 60,
          left: SZ.left,
          right: SZ.right,
          textAlign: "center",
          opacity: interpolate(frame, [0, 20], [0, 1], CLAMP),
          transform: `translateX(${interpolate(frame, [0, 20], [200, 0], CLAMP)}px)`,
        }}
      >
        <div style={{fontSize: 20, fontWeight: 600, color: RED, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12, fontFamily: FONT_STACK}}>
          ⚡ DESAFIO DE HOJE
        </div>
        <div style={{fontSize: 60, fontWeight: 800, color: WHITE, letterSpacing: -2, lineHeight: 1.1, fontFamily: FONT_STACK}}>
          ABRE O
          <br />
          <span style={{color: ORANGE}}>EXTRATO BANCÁRIO</span>
        </div>
      </div>

      {/* Calculadora */}
      <div
        style={{
          position: "absolute",
          top: "58%",
          left: "50%",
          width: 860,
          height: 600,
          transform: `translate(-50%, -50%) translateX(${interpolate(cardSpring, [0, 1], [200, 0])}px)`,
          ...premiumCard,
          padding: 48,
          opacity: interpolate(frame, [15, 30], [0, 1], CLAMP),
        }}
      >
        <div style={{fontSize: 18, fontWeight: 600, color: ORANGE, letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center", marginBottom: 32, fontFamily: FONT_STACK}}>
          CALCULADORA RÁPIDA
        </div>

        {/* Input 1: Gasto marketing */}
        <div style={{marginBottom: 32, opacity: interpolate(frame, [50, 70], [0, 1], CLAMP)}}>
          <div style={{fontSize: 16, color: MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1, fontFamily: FONT_STACK}}>
            Gasto em Marketing (último mês)
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.04)",
              border: `2px solid rgba(255, 107, 53, 0.3)`,
              borderRadius: 16,
              padding: 24,
              fontSize: 56,
              fontWeight: 700,
              color: ORANGE,
              textAlign: "center",
              fontFamily: "SF Mono, Menlo, monospace",
            }}
          >
            R${" "}
            {interpolate(frame, [80, 120], [0, 12500], CLAMP)
              .toFixed(0)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
          </div>
        </div>

        <div style={{textAlign: "center", fontSize: 48, color: DARK_MUTED, marginBottom: 32, opacity: interpolate(frame, [130, 145], [0, 1], CLAMP)}}>
          ÷
        </div>

        {/* Input 2: Clientes novos */}
        <div style={{marginBottom: 32, opacity: interpolate(frame, [150, 170], [0, 1], CLAMP)}}>
          <div style={{fontSize: 16, color: MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1, fontFamily: FONT_STACK}}>
            Clientes Novos (que pagaram)
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.04)",
              border: `2px solid rgba(0, 223, 130, 0.3)`,
              borderRadius: 16,
              padding: 24,
              fontSize: 56,
              fontWeight: 700,
              color: GREEN,
              textAlign: "center",
              fontFamily: "SF Mono, Menlo, monospace",
            }}
          >
            {interpolate(frame, [180, 210], [0, 37], CLAMP).toFixed(0)}
          </div>
        </div>

        {/* Resultado */}
        <div
          style={{
            background: "linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(248, 113, 113, 0.15) 100%)",
            border: `2px solid rgba(255, 107, 53, 0.5)`,
            borderRadius: 20,
            padding: 32,
            textAlign: "center",
            opacity: interpolate(frame, [230, 250], [0, 1], CLAMP),
            transform: `scale(${interpolate(resultSpring, [0, 1], [0.9, 1]) * resultPulse})`,
            boxShadow: `0 0 0 ${interpolate(Math.sin(frame * 0.12), [-1, 1], [0, 6], CLAMP)}px ${ORANGE}60`,
          }}
        >
          <div style={{fontSize: 18, color: MUTED, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1.5, fontFamily: FONT_STACK}}>
            CUSTO POR CLIENTE
          </div>
          <div style={{fontSize: 72, fontWeight: 700, color: WHITE, fontFamily: "SF Mono, Menlo, monospace"}}>
            R$ {interpolate(frame, [260, 300], [0, 338], CLAMP).toFixed(0)}
          </div>
        </div>
      </div>

      {/* Contexto inferior */}
      <div
        style={{
          position: "absolute",
          bottom: SZ.bottom + 20,
          left: 80,
          right: 80,
          textAlign: "center",
          opacity: interpolate(frame, [320, 340], [0, 1], CLAMP),
        }}
      >
        <div style={{fontSize: 32, fontWeight: 600, color: WHITE, lineHeight: 1.4, fontFamily: FONT_STACK}}>
          Se doer, <span style={{color: ORANGE}}>bom</span>.
          <br />
          <span style={{color: MUTED, fontSize: 28, fontWeight: 400}}>
            Dor é dado. Dado é decisão inteligente.
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA — Seed próximo episódio + perfil real ────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const profileSpring = spring({frame: frame - 10, fps, config: SPRINGS.snappy});
  const badgeSpring = spring({frame: frame - 60, fps, config: SPRINGS.bouncy});
  const ctaSpring = spring({frame: frame - 80, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill>
      <BG radial="rgba(255,107,53,0.1)" />
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: 900,
        }}
      >
        {/* Seed text */}
        <div
          style={{
            fontSize: 44,
            fontWeight: 600,
            color: WHITE,
            lineHeight: 1.3,
            marginBottom: 32,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [0, 20], [0, 1], CLAMP),
            transform: `translateY(${interpolate(frame, [0, 20], [30, 0], CLAMP)}px)`,
          }}
        >
          Amanhã a gente fala
          <br />
          do <span style={{color: ORANGE, fontSize: 56, fontWeight: 800}}>segundo número</span>
        </div>

        {/* Divider */}
        <div
          style={{
            width: 200,
            height: 2,
            background: "rgba(255, 107, 53, 0.3)",
            margin: "0 auto 32px",
            opacity: interpolate(frame, [25, 40], [0, 1], CLAMP),
          }}
        />

        {/* "Esse foi o seu" */}
        <div
          style={{
            fontSize: 36,
            fontWeight: 400,
            color: MUTED,
            marginBottom: 24,
            fontFamily: FONT_STACK,
            opacity: interpolate(frame, [35, 50], [0, 1], CLAMP),
          }}
        >
          Esse foi o seu
        </div>

        {/* Badge Café com teu ROI */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            padding: "20px 40px",
            background: "rgba(255, 107, 53, 0.15)",
            border: "2px solid rgba(255, 107, 53, 0.4)",
            borderRadius: 28,
            opacity: interpolate(frame, [55, 70], [0, 1], CLAMP),
            transform: `scale(${interpolate(badgeSpring, [0, 1], [0.9, 1])})`,
            marginBottom: 32,
          }}
        >
          <div style={{fontSize: 48}}>☕</div>
          <div>
            <div style={{fontSize: 16, fontWeight: 600, color: ORANGE, letterSpacing: 2, textTransform: "uppercase", fontFamily: FONT_STACK}}>
              CAFÉ COM
            </div>
            <div style={{fontSize: 32, fontWeight: 900, color: WHITE, letterSpacing: -1, fontFamily: FONT_STACK}}>
              TEU ROI
            </div>
          </div>
        </div>

        <br />

        {/* Perfil real */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            marginTop: 16,
            opacity: interpolate(profileSpring, [0, 1], [0, 1]),
            transform: `translateY(${interpolate(profileSpring, [0, 1], [20, 0])}px)`,
          }}
        >
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              overflow: "hidden",
              border: `3px solid ${ORANGE}`,
              boxShadow: `0 0 20px rgba(255,107,53,0.3)`,
            }}
          >
            <Img
              src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
              style={{width: "100%", height: "100%", objectFit: "cover"}}
            />
          </div>
          <div>
            <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
              @guilhermeribeiro.ai
            </div>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>
              Crescimento previsível
            </div>
          </div>
        </div>

        {/* CTA */}
        <div
          style={{
            marginTop: 24,
            fontSize: 28,
            fontWeight: 500,
            color: MUTED,
            fontFamily: FONT_STACK,
            opacity: interpolate(ctaSpring, [0, 1], [0, 1]),
          }}
        >
          Até amanhã. ☕
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE FADE WRAPPER ────────────────────────────────────────────
const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 10], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 12, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

// ─── MAIN COMPOSITION ──────────────────────────────────────────────
export const CafeEp08: React.FC = () => {
  let offset = 0;
  const SceneComponents = [S1, S2, S3, S4, S5];

  return (
    <AbsoluteFill style={{backgroundColor: BLACK}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, rgba(255,107,53,0.06), ${BLACK} 70%)`}} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.6} />

      {SCENES.map(({dur}, i) => {
        const from = offset;
        offset += dur;
        const Comp = SceneComponents[i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <Comp />
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
